/**
 * Blob and base64 encoding utilities for image/attachment handling.
 */

/**
 * Converts a base64 data URL to a Blob.
 * Returns null if the base64 data URL is malformed or contains invalid base64 content.
 *
 * @param base64DataUrl - The base64 data URL (e.g., 'data:image/png;base64,...')
 * @returns Blob instance or null if the input is invalid
 */
export function base64ToBlob(base64DataUrl: string): Blob | null {
  const [header, base64Data] = base64DataUrl.split(',');
  if (!base64Data) {
    return null;
  }

  const dataUrlMatch = /^data:([^;]+);base64$/i.exec(header);
  if (!dataUrlMatch) {
    return null;
  }

  const [, mimeType = 'image/png'] = dataUrlMatch;

  let byteString: string;
  try {
    byteString = atob(base64Data);
  } catch {
    // Invalid base64 content
    return null;
  }
  const arrayBuffer = new ArrayBuffer(byteString.length);
  const uint8Array = new Uint8Array(arrayBuffer);

  for (let i = 0; i < byteString.length; i++) {
    uint8Array[i] = byteString.codePointAt(i) ?? 0;
  }

  return new Blob([uint8Array], { type: mimeType });
}

/**
 * Converts a base64 data URL to FormData for upload.
 * Returns null if the data URL is malformed or base64 content is invalid.
 *
 * @param base64DataUrl - The base64 data URL
 * @returns FormData with 'data' key containing the blob, or null if invalid
 */
export function createThumbnailFormData(base64DataUrl: string): FormData | null {
  const blob = base64ToBlob(base64DataUrl);
  if (!blob) {
    return null;
  }
  const formData = new FormData();
  // Must include filename with proper extension for API to recognize MIME type
  formData.append('data', blob, 'thumbnail.png');
  return formData;
}
